import { country_enum } from '@predium/enums';
import { FieldPath, FieldValues } from 'react-hook-form';
import { PInputState } from '@/components/atoms/inputState';
import { isAlphanumericPostalCode } from '@/utils/formUtils';
import RHFNumberField from './RHFNumberField';
import RHFTextField from './RHFTextField';

type Props<TFieldValues extends FieldValues = FieldValues> = {
  name: FieldPath<TFieldValues>;
  label?: string;
  /** Country whose postal-code format decides which input variant to render. */
  country: country_enum | null | undefined;
  state?: PInputState;
};

/**
 * Postal-code input that adapts to the country's format.
 *
 * - Alphanumeric countries (e.g. UK "BT4 1GW") render a plain text field so letters aren't
 *   stripped, with an uppercase display transform.
 * - Digits-only countries (DE, AT, PL, FR) keep the numeric field (leading zeros allowed).
 */
export default function RHFPostalCodeField<TFieldValues extends FieldValues = FieldValues>({
  name,
  label,
  country,
  state,
}: Props<TFieldValues>) {
  if (isAlphanumericPostalCode(country)) {
    return (
      <RHFTextField<TFieldValues>
        name={name}
        label={label}
        state={state}
        inputProps={{ style: { textTransform: 'uppercase' } }}
      />
    );
  }

  return (
    <RHFNumberField<TFieldValues>
      name={name}
      label={label}
      state={state}
      allowDecimals={false}
      allowLeadingZeros
      valueAsText
    />
  );
}
